// client/extensions/Indent/Indent.ts
import { Extension, isList } from "@tiptap/core";
import { AllSelection, TextSelection } from "@tiptap/pm/state";
var IndentTranslations = {
  indent: {
    tooltip: {
      indent: ["Einzug vergr\xF6\xDFern", "Indent"],
      outdent: ["Einzug verringern", "Outdent"]
    }
  }
};
var IndentProps = /* @__PURE__ */ ((IndentProps2) => {
  IndentProps2[IndentProps2["max"] = 7] = "max";
  IndentProps2[IndentProps2["min"] = 0] = "min";
  IndentProps2[IndentProps2["increase"] = 1] = "increase";
  IndentProps2[IndentProps2["decrease"] = -1] = "decrease";
  return IndentProps2;
})(IndentProps || {});
var Indent = Extension.create({
  name: "indent",
  addOptions() {
    return {
      types: ["paragraph", "heading", "blockquote"],
      minIndent: 0 /* min */,
      maxIndent: 7 /* max */
    };
  },
  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          indent: {
            default: 0 /* min */,
            parseHTML: (element) => {
              const identAttr = element.getAttribute("indent-level");
              return (identAttr ? parseInt(identAttr, 10) : 0) || 0;
            },
            renderHTML: (attributes) => {
              if (!attributes.indent) {
                return {};
              }
              return { ["indent-level"]: attributes.indent };
            }
          }
        }
      }
    ];
  },
  addCommands() {
    return {
      indent: () => createIndentCommand({
        delta: 1 /* increase */,
        types: this.options.types
      }),
      outdent: () => createIndentCommand({
        delta: -1 /* decrease */,
        types: this.options.types
      })
    };
  },
  addKeyboardShortcuts() {
    return {
      Tab: () => this.editor.commands.indent(),
      "Shift-Tab": () => this.editor.commands.outdent()
    };
  }
});
function IndentExtension() {
  return {
    extensions: [Indent],
    styles(options) {
      const styles = new Array(7 /* max */ + 1).fill(0).map((_, idx) => {
        const key = `[indent-level="${idx}"]`;
        const margin = 2 * idx + "rem";
        return { [key]: { marginLeft: margin } };
      });
      return styles;
    },
    buttons: ({ editor, host }) => [
      {
        name: "indentIncrease",
        icon: "format_indent_increase",
        action: () => editor.commands.indent(),
        tooltip: host.translator.translate("tiptap", "indent", "tooltip", "indent")
      },
      {
        name: "indentDecrease",
        icon: "format_indent_decrease",
        action: () => editor.commands.outdent(),
        tooltip: host.translator.translate("tiptap", "indent", "tooltip", "outdent")
      }
    ]
  };
}
function createIndentCommand({ delta, types }) {
  return ({ state, dispatch, editor }) => {
    let { tr } = state;
    tr = updateIndentLevel(tr, delta, types, editor);
    if (tr.docChanged) {
      dispatch && dispatch(tr);
      return true;
    }
    return false;
  };
}
function updateIndentLevel(tr, delta, types, editor) {
  const { doc, selection } = tr;
  if (!doc || !selection)
    return tr;
  if (!(selection instanceof TextSelection || selection instanceof AllSelection)) {
    return tr;
  }
  const { from, to } = selection;
  doc.nodesBetween(from, to, (node, pos) => {
    const nodeType = node.type;
    if (types.includes(nodeType.name)) {
      tr = setNodeIndentMarkup(tr, pos, delta);
      return false;
    } else if (isList(node.type.name, editor.extensionManager.extensions)) {
      return false;
    }
    return true;
  });
  return tr;
}
function setNodeIndentMarkup(tr, pos, delta) {
  if (!tr.doc)
    return tr;
  const node = tr.doc.nodeAt(pos);
  if (!node)
    return tr;
  const minIndent = 0 /* min */;
  const maxIndent = 7 /* max */;
  const indent = clamp((node.attrs.indent || 0) + delta, minIndent, maxIndent);
  if (indent === node.attrs.indent)
    return tr;
  const nodeAttrs = {
    ...node.attrs,
    indent
  };
  return tr.setNodeMarkup(pos, node.type, nodeAttrs, node.marks);
}
function clamp(val, min, max) {
  if (val < min) {
    return min;
  }
  if (val > max) {
    return max;
  }
  return val;
}
export {
  IndentExtension,
  IndentProps,
  IndentTranslations,
  clamp,
  createIndentCommand
};
